import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Card, CardContent, Typography, Divider, CircularProgress } from '@material-ui/core';

import * as ActionsDialog from 'app/store/actions';
import * as Actions from './store/actions';
import { getAiFeaturesEnabledStatus } from 'app/main/settings/store/actions/general.actions';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles(() => ({
	logFont: {
		fontFamily: 'Ubuntu Mono'
	},
	logWrapper: {
		scrollBehavior: 'smooth'
	},
	aiAnalysisCard: {
		backgroundColor: '#f5f5f5',
		marginBottom: '16px',
		border: '1px solid #ddd'
	},
	aiAnalysisTitle: {
		color: '#1976d2',
		fontWeight: 'bold'
	},
	errorAnalysisText: {
		fontFamily: 'monospace',
		backgroundColor: '#fff',
		padding: '8px',
		border: '1px solid #ccc',
		borderRadius: '4px',
		fontSize: '12px'
	}
}));

const Logs = props => {
	const classes = useStyles();
	const dispatch = useDispatch();
		const logsRef = useRef(null);
		const logsEndRef = useRef(null);

	const [atBottom, setAtBottom] = useState(false);
	const [firstRender, setFirstRender] = useState(true);
	const [showFullLogs, setShowFullLogs] = useState(true); // Will be managed by useEffect after data loads
	const [hasBeenAutoHidden, setHasBeenAutoHidden] = useState(false);
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const rawLogs = useSelector(({ clusters }) => clusters.cluster.logs);
	const logsClusterId = useSelector(({ clusters }) => clusters.cluster.logsClusterId);
	// Only use logs if they belong to the current cluster (prevents log leakage between clusters)
	const logs = (logsClusterId === props.cluster?.id || logsClusterId === null) ? rawLogs : null;
	const aiErrorAnalysis = useSelector(({ clusters }) => clusters.cluster.aiErrorAnalysis);	const aiFeaturesEnabled = useSelector(({ settings }) => settings.settings.aiFeaturesEnabled);

	// Get live cluster data from Redux store instead of using static props
	// This ensures we get real-time updates when cluster state changes
	const currentCluster = useSelector(({ clusters }) => {
		// First try to get the single cluster if it matches the ID we're looking for
		const singleCluster = clusters.cluster.cluster;
		if (singleCluster?.id === props.cluster?.id) {
			return singleCluster;
		}
		
		// Otherwise, find it in the clusters list
		const clusterFromList = clusters.cluster.list?.find(c => c.id === props.cluster?.id);
		return clusterFromList || props.cluster; // Fallback to props if not found
	});

	const isClusterFailed = currentCluster?.info?.state === 'failed';
	const hasAnalysisForCluster = aiErrorAnalysis?.clusterId === currentCluster?.id;
	const hasAnyAnalysis = aiErrorAnalysis && aiErrorAnalysis.analysis;
	
	// If we have AI analysis for this cluster, treat it as "failed" for UI purposes
	// BUT only if the cluster is actually failed or deploying (not destroyed/down)
	const shouldShowAsFailedCluster = isClusterFailed || (hasAnalysisForCluster && (currentCluster?.info?.state === 'deploying' || currentCluster?.info?.state === 'failed'));

	// Fetch AI features status on component mount
	useEffect(() => {
		dispatch(getAiFeaturesEnabledStatus());
	}, [dispatch]);

	// Load cached AI analysis on component mount (separate from real-time logic)
	useEffect(() => {
		if (currentCluster?.id && logs) {
			dispatch(Actions.loadCachedAiAnalysis(currentCluster.id, logs));
		}
	}, [dispatch, currentCluster?.id]); // Only run on mount, not when logs or isClusterFailed change

	// Handle initial state - hide logs if this is a failed cluster with AI enabled
	useEffect(() => {
		if (aiFeaturesEnabled !== null && aiFeaturesEnabled && isClusterFailed) {
			setShowFullLogs(false);
			setHasBeenAutoHidden(true);
		}
	}, [aiFeaturesEnabled, isClusterFailed]);

	// This effect ensures that when a cluster fails, the logs are automatically
	// hidden to show the AI analysis, but only once. If the user chooses to
	// see the full logs again, they will not be re-hidden on subsequent renders.
	// Also clears AI analysis when cluster is destroyed/down.
	useEffect(() => {
		const clusterState = currentCluster?.info?.state;
		
		// Clear AI analysis if cluster is destroyed/down
		if (clusterState === 'down' || clusterState === 'destroyed') {
			dispatch(Actions.clearAiErrorAnalysis(currentCluster?.id));
			setShowFullLogs(true);
			setHasBeenAutoHidden(false);
			return;
		}
		
		if (aiFeaturesEnabled && shouldShowAsFailedCluster && hasAnyAnalysis && !hasBeenAutoHidden) {
			setShowFullLogs(false);
			setHasBeenAutoHidden(true);
		} else if (!shouldShowAsFailedCluster) {
			// If the cluster is not in a failed state (e.g., deploying), we ensure
			// the logs are visible and reset the auto-hide flag for future failures.
			setShowFullLogs(true);
			setHasBeenAutoHidden(false);
		}
	}, [aiFeaturesEnabled, shouldShowAsFailedCluster, hasAnyAnalysis, hasBeenAutoHidden, currentCluster?.info?.state, dispatch, currentCluster?.id]);
	
	// Automatically trigger AI analysis for failed clusters when logs are available
	useEffect(() => {


		if (aiFeaturesEnabled && isClusterFailed && logs && !hasAnalysisForCluster && !isAnalyzing) {
			setIsAnalyzing(true);
			dispatch(Actions.analyzeTerraformError(currentCluster.id, logs))
				.then(result => {
					setIsAnalyzing(false);
	
				})
				.catch(err => {
					console.error(err);
					setIsAnalyzing(false);
				});
		}
	}, [aiFeaturesEnabled, isClusterFailed, logs, hasAnalysisForCluster, isAnalyzing, dispatch, currentCluster?.id]);

		const closeLogs = () => {
			dispatch(Actions.clearLogs());
			dispatch(ActionsDialog.closeDialog());
		};

		const scrollToBottom = () => {
			if (logsRef.current) {
			  logsRef.current.scrollTop = logsRef.current.scrollHeight + 200;
			}
		  };

		const setBottom = ({ target }) => {
			const { scrollHeight, scrollTop, clientHeight } = target;
			const threshold = 100;
		  
			setAtBottom(scrollHeight - scrollTop <= clientHeight + threshold);
		  };

		useEffect(() => {
			if (firstRender && logs) {
				scrollToBottom();
				setFirstRender(false);
			}
		}, [firstRender, setFirstRender, logs]);

		useEffect(() => {
			const { type } = props;
			const timer = setTimeout(
				() => dispatch(Actions.getLogsRepeat({ id: currentCluster.id, type })).then(() => atBottom && scrollToBottom()),
				10000
			);
			return () => clearTimeout(timer);
		}, [dispatch, props, logs, atBottom, currentCluster?.id]); // leave logs in in order to trigger action
		return (
	<React.Fragment>
		{(!shouldShowAsFailedCluster || showFullLogs || !aiFeaturesEnabled) ? (
		<DialogTitle className="border-b-1">
			<div className="flex flex-row items-center justify-between">
				<span id="logs-header">{`Logs for ${currentCluster?.name || 'Cluster'}`}</span>
				<Button id="scroll-btn" variant="outlined" type="button" onClick={scrollToBottom}>
					Scroll To Bottom
				</Button>
			</div>
		</DialogTitle>
		) : 
		<div className='my-32' id="logs-header"/>
		}

			<DialogContent ref={logsRef} className={classes.logWrapper} onScroll={e => setBottom(e)}>
				{/* AI Analysis Progress Indicator - Only show if AI features are enabled */}
				{aiFeaturesEnabled && isAnalyzing && (
					<Card className={classes.aiAnalysisCard}>
						<CardContent>
							<Typography variant="h6" className={classes.aiAnalysisTitle}>
								<span role="img" aria-label="robot">🤖</span> Error Analysis
								<CircularProgress size={20} style={{ marginLeft: '8px' }} />
							</Typography>
							<Divider style={{ margin: '8px 0' }} />
							<Typography variant="body2" color="primary">
								Analyzing error... This may take a while.
							</Typography>
						</CardContent>
					</Card>
				)}
						{/* AI Error Analysis Results - Show prominently when we have analysis for this cluster */}
			{aiFeaturesEnabled && !isAnalyzing && hasAnalysisForCluster && hasAnyAnalysis && (
					<Card className={classes.aiAnalysisCard}>
						<CardContent>
							<Typography variant="h6" className={classes.aiAnalysisTitle}>
								<span role="img" aria-label="robot">🤖</span> Error Analysis
							</Typography>
							<Divider style={{ margin: '8px 0' }} />
							<Typography variant="body1" style={{ 
								marginBottom: '16px', 
								fontSize: '16px', 
								lineHeight: '1.6',
								fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
								color: '#2c3e50'
							}}>
								{aiErrorAnalysis.analysis?.data?.response || 
								 aiErrorAnalysis.analysis?.response || 
								 aiErrorAnalysis.analysis?.text || 
								 aiErrorAnalysis.analysis?.message || 
								 aiErrorAnalysis.analysis?.content ||
								 (typeof aiErrorAnalysis.analysis === 'string' ? aiErrorAnalysis.analysis : null) ||
								 'Analysis completed'}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								Analysis generated at: {new Date(aiErrorAnalysis.timestamp).toLocaleString()}
							</Typography>
						</CardContent>
					</Card>
				)}		{/* Show Full Logs Button - For failed clusters with AI enabled: show when logs are hidden */}
		{aiFeaturesEnabled && shouldShowAsFailedCluster && !showFullLogs && (
			<div style={{ textAlign: 'center', marginBottom: '16px' }}>
				<Button 
					variant="outlined" 
					onClick={() => setShowFullLogs(true)}
					size="large"
				>
					📋 Show Full Logs
				</Button>
			</div>
		)}

		{/* Full Logs - Show based on explicit logic */}
		{(!shouldShowAsFailedCluster || !aiFeaturesEnabled || showFullLogs) && (
				<>
					
					{/* Hide Logs Button - Only show for failed clusters when AI features are enabled */}
					{aiFeaturesEnabled && shouldShowAsFailedCluster && showFullLogs && (
						<div style={{ textAlign: 'center', marginBottom: '16px' }}>
							<Button 
								variant="outlined" 
								onClick={() => setShowFullLogs(false)}
								size="small"
							>
								🔼 Hide Logs
							</Button>
						</div>
					)}
						
						{/* Full logs */}
						<div id="logs" className={classes.logFont} dangerouslySetInnerHTML={{ __html: logs }}></div>
						<div ref={logsEndRef} />
					</>
				)}
			</DialogContent>
			
			<DialogActions className="border-t-1">
				<Button id="logs-close-btn" onClick={closeLogs} variant="outlined">
					Close
				</Button>
			</DialogActions>
		</React.Fragment>
	);
};

export default Logs;
